import { useState, useEffect, useCallback } from 'react';
import { Text, Card, Button } from '@hawkins/components';
import NetflixGradientBackground from 'src/components/elements/NetflixGradientBackground';

interface EnvironmentInfo {
  timestamp: string;
  environment: string;
  environmentVariables: Record<string, string>;
  pandoraClient: {
    environment: string;
    hostname: string;
    port: number;
  };
  oktaBridgeClient: {
    environment: string;
    hostname: string;
    port: number;
  };
  mfaConfig: {
    clientId: string;
    issuer: string;
    authorizationEndpoint: string;
    tokenEndpoint: string;
    maxTokenAge: number;
    clockSkew: number;
  };
}

const EnvironmentConfigDisplay = ({ environmentInfo }: { 
  environmentInfo?: EnvironmentInfo;
}) => (
  <Card style={{
    backgroundColor: '#1a1a2e',
    border: '1px solid #16213e',
    borderRadius: '4px',
    padding: '20px',
    marginBottom: '20px'
  }}>
    <Text style={{ color: '#e94560', fontSize: '16px', marginBottom: '16px' }}>
      🔧 Environment Configuration ({environmentInfo?.environment})
    </Text>
    
    {/* Environment Variables */}
    <div style={{ marginBottom: '20px' }}>
      <Text style={{ color: '#f5f5f5', fontSize: '14px', marginBottom: '12px' }}>
        Environment Variables:
      </Text>
      <div style={{ paddingLeft: '16px' }}>
        {environmentInfo && Object.entries(environmentInfo.environmentVariables).map(([key, value]) => (
          <div key={key} style={{ marginBottom: '8px' }}>
            <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
              {key}:
            </Text>
            <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
              {value}
            </Text>
          </div>
        ))}
      </div>
    </div>

    {/* Pandora Client */}
    <div style={{ marginBottom: '20px' }}>
      <Text style={{ color: '#f5f5f5', fontSize: '14px', marginBottom: '12px' }}>
        Pandora API:
      </Text>
      <div style={{ paddingLeft: '16px' }}>
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Environment:
          </Text>
          <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
            {environmentInfo?.pandoraClient.environment}
          </Text>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Hostname:
          </Text>
          <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
            {environmentInfo?.pandoraClient.hostname}
          </Text>
        </div>
        <div>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Port:
          </Text>
          <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
            {environmentInfo?.pandoraClient.port}
          </Text>
        </div>
      </div>
    </div>

    {/* OktaBridge Client */}
    <div style={{ marginBottom: '20px' }}>
      <Text style={{ color: '#f5f5f5', fontSize: '14px', marginBottom: '12px' }}>
        OktaBridge API:
      </Text>
      <div style={{ paddingLeft: '16px' }}>
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Environment:
          </Text>
          <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
            {environmentInfo?.oktaBridgeClient.environment}
          </Text>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Hostname:
          </Text>
          <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
            {environmentInfo?.oktaBridgeClient.hostname}
          </Text>
        </div>
        <div>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Port:
          </Text>
          <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
            {environmentInfo?.oktaBridgeClient.port}
          </Text>
        </div>
      </div>
    </div>

    {/* MFA Configuration */}
    <div>
      <Text style={{ color: '#f5f5f5', fontSize: '14px', marginBottom: '12px' }}>
        MFA Configuration:
      </Text>
      <div style={{ paddingLeft: '16px' }}>
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Client ID:
          </Text>
          <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
            {environmentInfo?.mfaConfig.clientId}
          </Text>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Issuer:
          </Text>
          <div style={{ marginLeft: '12px', wordBreak: 'break-all', overflowWrap: 'break-word' }}>
            <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace' }}>
              {environmentInfo?.mfaConfig.issuer}
            </Text>
          </div>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Authorization Endpoint:
          </Text>
          <div style={{ marginLeft: '12px', wordBreak: 'break-all', overflowWrap: 'break-word' }}>
            <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace' }}>
              {environmentInfo?.mfaConfig.authorizationEndpoint}
            </Text>
          </div>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Token Endpoint:
          </Text>
          <div style={{ marginLeft: '12px', wordBreak: 'break-all', overflowWrap: 'break-word' }}>
            <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace' }}>
              {environmentInfo?.mfaConfig.tokenEndpoint}
            </Text>
          </div>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Max Token Age:
          </Text>
          <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
            {environmentInfo?.mfaConfig.maxTokenAge}s
          </Text>
        </div>
        <div>
          <Text style={{ color: '#0f4c75', fontSize: '13px', fontFamily: 'monospace' }}>
            Clock Skew:
          </Text>
          <Text style={{ color: '#3282b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '12px' }}>
            {environmentInfo?.mfaConfig.clockSkew}s
          </Text>
        </div>
      </div>
    </div>
  </Card>
);

const NetflixLogo = () => (
  <div style={{ marginBottom: '40px' }}>
    <img 
      src="/nflx-logo-classic@150w.png" 
      alt="Netflix" 
      style={{ height: '80px' }}
    />
  </div>
);

function DebugPage() {
  const [environmentInfo, setEnvironmentInfo] = useState<EnvironmentInfo | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchEnvironmentInfo = useCallback(async () => {
    setLoading(true);
    
    try {
      const response = await fetch('/api/debug/environment-info', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setEnvironmentInfo(data.environmentInfo);
        console.log('Environment info retrieved:', data.environmentInfo);
      } else {
        console.error('Environment info failed:', data.error);
      }
    } catch (error) {
      console.error('Error fetching environment info:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnvironmentInfo();
  }, [fetchEnvironmentInfo]);

  return (
    <NetflixGradientBackground>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        paddingTop: '40px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '600px'
        }}>
          <NetflixLogo />

          <Card style={{
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            padding: '48px 68px 36px',
            borderRadius: '4px',
            width: '100%',
            border: 'none'
          }}>
            <Text 
              style={{ 
                color: 'white', 
                fontSize: '28px',
                fontWeight: '700',
                marginBottom: '24px',
                textAlign: 'center'
              }}
            >
              Debug Information
            </Text>

            <EnvironmentConfigDisplay environmentInfo={environmentInfo || undefined} />

            <Button
              onClick={fetchEnvironmentInfo}
              disabled={loading}
              style={{
                width: '100%',
                backgroundColor: '#0f4c75',
                border: 'none',
                borderRadius: '4px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                padding: '12px 16px',
                cursor: 'pointer',
                height: '48px',
                minHeight: '48px',
                boxSizing: 'border-box'
              }}
            >
              {loading ? 'Updating...' : 'Refresh Environment Info'}
            </Button>
          </Card>
        </div>
      </div>
    </NetflixGradientBackground>
  );
}

export default DebugPage;